import PenIcon from "~icons/local/pen";
import ClearIcon from "~icons/fa6-solid/x";
import { useState } from "react";
import RowLayout from "./lib/RowLayout";
import { quantityColumnWidth } from "./constants";
import useOperationListContext from "./lib/useOperationListContext";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import useUpdatePart from "@/lib/queries/useUpdatePart";
import Button from "@/lib/Button";
import { components } from "@/lib/ApiSchema";
import ConfirmationModal from "@/lib/modal/ConfirmationModal";
import useModal from "@/lib/modal/useModal";
import Select from "react-select";
import { useParams } from "react-router";
import { useResetOperations } from "@/lib/queries/hooks";
import { Tooltip } from "react-tooltip";
import Spinner from "@/lib/Spinner";
import PriorPartEstimatesModal from "../ListPriorEstimatesModal";

export default function HeaderRow({
  part,
  preventEdit,
  calculatorCreationInProgress,
}: {
  part: components["schemas"]["RequestedPartWithOperationsSchema"];
  preventEdit: boolean;
  calculatorCreationInProgress: boolean;
}) {
  const { updatePart, isPending } = useUpdatePart();
  const { quantities } = useOperationListContext();
  const [editing, setEditing] = useState(false);
  const [quantitiesInEdit, setQuantitiesInEdit] = useState(quantities);
  const [input, setInput] = useState("");
  const canEditQuoteRevision = useGetCurrentQuoteRevision()?.can_edit;

  if (editing) {
    return (
      <div className="w-full py-2 flex gap-1 justify-center text-xs bg-slate-100">
        <div className="flex divide-x divide-slate-100 text-xs overflow-x-auto bg-white rounded-sm">
          {quantitiesInEdit.map((q, i) => (
            <div
              key={i}
              className="flex justify-center items-center gap-2"
              style={{
                maxWidth: quantityColumnWidth,
                minWidth: quantityColumnWidth,
              }}
            >
              {q}
              <ClearIcon
                className="cursor-pointer text-slate-400 hover:text-slate-600 size-3"
                onClick={() =>
                  setQuantitiesInEdit((pre) => pre.filter((n) => n !== q))
                }
              />
            </div>
          ))}
        </div>
        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="w-[76px] px-1 bg-white"
          placeholder="add quantity"
          onKeyUp={(e) => {
            if (e.key === "Enter") addQuantity();
            else if (e.key === "Escape") setInput("");
          }}
          onBlur={addQuantity}
        />
        <Button
          primary
          size="sm"
          loading={isPending}
          onClick={() => {
            updatePart({
              payload: { requested_quantities: quantitiesInEdit },
              onSuccess: () => {
                setEditing(false);
              },
            });
          }}
          className="cursor-pointer px-2 bg-blue-600 text-white hover:bg-blue-500 rounded-sm"
        >
          Done
        </Button>
      </div>
    );
  }

  return (
    <RowLayout
      left={
        <Left
          part={part}
          calculatorCreationInProgress={calculatorCreationInProgress}
        />
      }
      right={<Right />}
      actions={
        preventEdit || !canEditQuoteRevision ? null : (
          <PenIcon
            className="cursor-pointer"
            onClick={() => setEditing(true)}
          />
        )
      }
      className="sticky top-0 z-10"
    />
  );

  function Left({
    part,
    calculatorCreationInProgress,
  }: {
    part: components["schemas"]["RequestedPartWithOperationsSchema"];
    calculatorCreationInProgress: boolean;
  }) {
    const { teamSlug } = useParams();
    const { mutate: resetOperations } = useResetOperations();
    const { showModal, closeModal } = useModal();

    type ValueType = components["schemas"]["NamedWorkflowProcess"] | "_import";

    const options: {
      label: string;
      value: ValueType;
    }[] = [
      { label: "CNC Milling", value: "CNC_Milling" },
      { label: "CNC Turning", value: "CNC_Turning" },
      { label: "Sheet Metal", value: "Sheet" },
      { label: "Assembly", value: "Assembly" },
      { label: "Purchased Part", value: "OTS" },
      // { label: "Part+Hardware", value: "Part_With_Hardware" },  // TODO: this is currently empty so it's imo better to not show it
      { label: "No Operations", value: "Empty" },
      { label: "Import from prior estimates", value: "_import" },
    ];

    const selectedProcess = part.selected_process as ValueType;

    return (
      <div className="flex items-center gap-2">
        <Select
          classNames={{
            container: () => "min-w-36 ml-1",
            menuList: () => "min-h-88",
          }}
          options={options}
          isDisabled={part.will_bid !== null || calculatorCreationInProgress}
          value={{
            label: options.find((o) => o.value === part.selected_process)
              ?.label,
            value: selectedProcess,
          }}
          onChange={(option) => {
            if (!option || option.value === part.selected_process) return;

            if (option.value === "_import") {
              showModal(
                <PriorPartEstimatesModal
                  initialSearch={part.part_number}
                  currentPartId={part.id}
                  close={closeModal}
                />,
              );
            } else {
              showModal(
                <ConfirmationModal
                  onCancel={closeModal}
                  onConfirm={() => {
                    resetOperations({
                      params: {
                        path: {
                          team_slug: teamSlug!,
                          id: part.id,
                        },
                      },
                      body: {
                        selected_process:
                          option.value as components["schemas"]["NamedWorkflowProcess"],
                      },
                    });
                    closeModal();
                  }}
                >
                  Changes made to the current
                  <br /> estimate will be lost
                </ConfirmationModal>,
              );
            }
          }}
        />

        <Spinner
          show={calculatorCreationInProgress}
          className="size-4 text-slate-400"
          data-tooltip-id="in-progress-tooltip"
        />

        <Tooltip id="in-progress-tooltip">
          Part estimation is being calculated.
        </Tooltip>
      </div>
    );
  }

  function Right() {
    return (
      <div className="flex divide-x divide-slate-100 text-sm">
        {quantities.map((q, i) => (
          <div
            key={i}
            className="flex justify-center items-center"
            style={{
              maxWidth: quantityColumnWidth,
              minWidth: quantityColumnWidth,
            }}
          >
            {q}
          </div>
        ))}
      </div>
    );
  }

  function addQuantity() {
    const n = parseInt(input, 10);
    if (n && !quantitiesInEdit.includes(n)) {
      setQuantitiesInEdit((pre) =>
        [...pre, parseInt(input, 10)].sort((a, b) => a - b),
      );
      setInput("");
    }
  }
}
